import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Space,
  Tag,
  Rate,
  Divider,
  InputNumber,
  Tabs,
  List,
  Avatar,
  message,
  Spin,
  Breadcrumb,
  Input,
  Empty,
} from 'antd';
import {
  HeartOutlined,
  ShoppingCartOutlined,
  ShareAltOutlined,
  StarOutlined,
  HeartFilled,
  UserOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  QuestionCircleOutlined,
  SendOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  productService,
  cartService,
  wishlistService,
  reviewService,
  storageUrl,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const { Title, Paragraph, Text } = Typography;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [isFavorite, setIsFavorite] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [newQuestion, setNewQuestion] = useState('');
  const [submittingQuestion, setSubmittingQuestion] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  const requireAuth = () => {
    if (!isAuthenticated) {
      message.info('Please login or register to continue');
      navigate('/login', { state: { from: `/products/${id}` } });
      return false;
    }
    return true;
  };

  // Track recently viewed products
  useEffect(() => {
    if (product?.id) {
      try {
        const viewed = JSON.parse(localStorage.getItem('recentlyViewed') || '[]');
        const updated = [
          {
            id: product.id,
            name: product.name,
            price: product.final_price ?? product.sale_price ?? product.price,
            image: product.images?.[0]?.url || null,
            timestamp: Date.now(),
          },
          ...viewed.filter((v) => v.id !== product.id),
        ].slice(0, 10);
        localStorage.setItem('recentlyViewed', JSON.stringify(updated));
      } catch {
        /* ignore */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await productService.getProductDetail(id);
        const raw = response.data?.data || response.data;
        setProduct({
          ...raw,
          category: typeof raw.category === 'object' ? raw.category?.name || '' : raw.category,
          reviews: Array.isArray(raw.reviews) ? raw.reviews : [],
          images: Array.isArray(raw.images)
            ? raw.images.map((img) => ({ ...img, url: img.url || img.image_url || '' }))
            : [],
          relatedProducts: Array.isArray(raw.relatedProducts) ? raw.relatedProducts : [],
          price: parseFloat(raw.price) || 0,
          original_price: raw.original_price ? parseFloat(raw.original_price) : null,
          originalPrice: raw.originalPrice ? parseFloat(raw.originalPrice) : null,
          rating: parseFloat(raw.rating) || 0,
        });
      } catch (error) {
        setProduct({
          id,
          name: 'Product',
          category: 'General',
          price: 0,
          rating: 0,
          reviews: [],
          is_available: true,
          inStock: true,
          stock: 0,
          description: 'Product description',
          features: [],
          specifications: {},
          images: [],
          relatedProducts: [],
          original_price: null,
          originalPrice: null,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleAddToCart = async () => {
    if (!requireAuth()) return;
    try {
      await cartService.addToCart(id, quantity);
      message.success('Added to cart!');
    } catch (error) {
      message.error('Failed to add to cart');
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'NPR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price || 0);
  };

  const [wishlistId, setWishlistId] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) return;
    const checkWishlist = async () => {
      try {
        const res = await wishlistService.getWishlist();
        const items = Array.isArray(res.data) ? res.data : res.data?.data || [];
        const found = items.find(
          (item) => item.product_service_id === id || item.product_service?.id === id,
        );
        if (found) {
          setIsFavorite(true);
          setWishlistId(found.id);
        }
      } catch (_) {}
    };
    checkWishlist();
  }, [id, isAuthenticated]);

  const handleAddToWishlist = async () => {
    if (!requireAuth()) return;
    try {
      if (isFavorite) {
        await wishlistService.removeFromWishlist(wishlistId || id);
      } else {
        await wishlistService.addToWishlist(id);
      }
      setIsFavorite(!isFavorite);
      message.success(isFavorite ? 'Removed from wishlist' : 'Added to wishlist!');
    } catch (error) {
      message.error('Failed to update wishlist');
    }
  };

  const fetchQuestions = async () => {
    try {
      const res = await reviewService.getProductQuestions(id);
      setQuestions(res.data?.data || []);
    } catch {
      /* ignore */
    }
  };

  const handleAskQuestion = async () => {
    if (!requireAuth()) return;
    if (!newQuestion.trim()) return;
    setSubmittingQuestion(true);
    try {
      await reviewService.askProductQuestion(id, newQuestion.trim());
      message.success('Question submitted!');
      setNewQuestion('');
      fetchQuestions();
    } catch (error) {
      message.error('Failed to submit question');
    } finally {
      setSubmittingQuestion(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!requireAuth()) return;
    setSubmittingReview(true);
    try {
      await reviewService.createReview({
        product_service_id: id,
        rating: reviewRating,
        comment: reviewComment,
      });
      message.success('Review submitted!');
      setShowReviewForm(false);
      setReviewComment('');
      setReviewRating(5);
      const res = await productService.getProductDetail(id);
      const raw = res.data?.data || res.data;
      if (Array.isArray(raw.reviews)) setProduct((prev) => ({ ...prev, reviews: raw.reviews }));
    } catch (error) {
      const errMsg = error.response?.data?.message || 'Failed to submit review';
      message.error(errMsg);
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading)
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  if (!product)
    return (
      <div style={{ padding: '24px' }}>
        <Title level={4}>Product not found</Title>
      </div>
    );

  return (
    <div style={{ padding: '24px' }}>
      <Breadcrumb
        style={{ marginBottom: '24px' }}
        items={[
          { title: <Link to="/">Home</Link> },
          { title: <Link to="/products">Products</Link> },
          { title: product.name },
        ]}
      />

      <Row gutter={[24, 24]}>
        <Col xs={24} md={12}>
          <Card>
            <div
              style={{
                width: '100%',
                height: '400px',
                backgroundColor: '#f0f0f0',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px',
              }}
            >
              {product.images?.length > 0 ? (
                <img
                  src={storageUrl(product.images[0].url || product.images[0].image_url)}
                  alt={product.name}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              ) : (
                <div style={{ fontSize: '64px' }}>🏥</div>
              )}
            </div>
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card>
            <div style={{ marginBottom: '16px' }}>
              <Tag color="blue">{product.category}</Tag>
              <Tag color={product.is_available || product.inStock ? 'green' : 'red'}>
                {product.is_available || product.inStock ? 'In Stock' : 'Out of Stock'}
              </Tag>
            </div>
            <Title level={2}>{product.name}</Title>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}
            >
              <Rate disabled defaultValue={product.rating} />
              <Text>({product.total_reviews || product.reviews?.length || 0} reviews)</Text>
            </div>
            <Text style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2d5a3d' }}>
              {product.final_price === 0
                ? 'Free'
                : formatPrice(product.final_price ?? product.price)}
            </Text>
            {(product.final_price ?? product.price) < product.price && (
              <>
                <Text
                  type="secondary"
                  style={{ textDecoration: 'line-through', marginLeft: '8px' }}
                >
                  {formatPrice(product.price)}
                </Text>
                <Tag color="red" style={{ marginLeft: 8, fontSize: 13 }}>
                  {product.final_price === 0
                    ? 'FREE for Members'
                    : `${Math.round((1 - (product.final_price ?? product.price) / product.price) * 100)}% OFF`}
                </Tag>
              </>
            )}
            <Divider />
            <Paragraph>{product.description}</Paragraph>
            <div style={{ marginBottom: '24px' }}>
              <Text strong>Quantity:</Text>
              <InputNumber
                min={1}
                max={product.stock || 99}
                value={quantity}
                onChange={setQuantity}
                style={{ width: '80px', marginLeft: '8px' }}
              />
            </div>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                type="primary"
                size="large"
                icon={<ShoppingCartOutlined />}
                onClick={handleAddToCart}
                disabled={!(product.is_available ?? product.inStock)}
                block
              >
                Add to Cart
              </Button>
              <Space>
                <Button
                  icon={isFavorite ? <HeartFilled /> : <HeartOutlined />}
                  onClick={handleAddToWishlist}
                >
                  {isFavorite ? 'Remove from Wishlist' : 'Add to Wishlist'}
                </Button>
                <Button
                  icon={<ShareAltOutlined />}
                  onClick={() => message.info('Share link copied!')}
                >
                  Share
                </Button>
              </Space>
            </Space>
          </Card>
        </Col>
      </Row>

      <Row gutter={24} style={{ marginTop: 24 }}>
        <Col xs={24} lg={16}>
          <Card>
            <Tabs
              defaultActiveKey="description"
              items={[
                {
                  key: 'description',
                  label: 'Description',
                  children: <Paragraph>{product.description}</Paragraph>,
                },
                {
                  key: 'reviews',
                  label: `Reviews (${product.reviews?.length || 0})`,
                  children: (
                    <div>
                      <div style={{ marginBottom: 16, textAlign: 'right' }}>
                        <Button
                          icon={<StarOutlined />}
                          onClick={() => {
                            if (requireAuth()) setShowReviewForm(!showReviewForm);
                          }}
                        >
                          {showReviewForm ? 'Cancel' : 'Write a Review'}
                        </Button>
                      </div>
                      {showReviewForm && (
                        <Card
                          size="small"
                          style={{
                            marginBottom: 16,
                            background: '#fafff5',
                            border: '1px solid #e8f0e8',
                          }}
                        >
                          <Space direction="vertical" style={{ width: '100%' }}>
                            <div>
                              <Text strong>Rating: </Text>
                              <Rate value={reviewRating} onChange={setReviewRating} />
                            </div>
                            <Input.TextArea
                              rows={3}
                              placeholder="Share your experience..."
                              value={reviewComment}
                              onChange={(e) => setReviewComment(e.target.value)}
                            />
                            <Button
                              type="primary"
                              loading={submittingReview}
                              onClick={handleSubmitReview}
                              icon={<SendOutlined />}
                              disabled={!reviewRating}
                            >
                              Submit Review
                            </Button>
                          </Space>
                        </Card>
                      )}
                      {product.reviews?.length > 0 ? (
                        <List
                          dataSource={product.reviews}
                          renderItem={(review) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={<Avatar icon={<UserOutlined />} />}
                                title={
                                  <Space>
                                    {review.user?.name ||
                                      review.user?.full_name ||
                                      review.user_name ||
                                      'Anonymous'}
                                    <Rate
                                      disabled
                                      defaultValue={parseFloat(review.rating)}
                                      style={{ fontSize: 14 }}
                                    />
                                  </Space>
                                }
                                description={
                                  <div>
                                    <Text type="secondary">
                                      {dayjs(review.date || review.created_at).format(
                                        'MMM D, YYYY',
                                      )}
                                    </Text>
                                    <Paragraph>{review.comment || review.review}</Paragraph>
                                  </div>
                                }
                              />
                            </List.Item>
                          )}
                        />
                      ) : (
                        <Empty description="No reviews yet. Be the first to review!" />
                      )}
                    </div>
                  ),
                },
                {
                  key: 'qa',
                  label: `Q&A (${questions.length})`,
                  children: (
                    <div>
                      {isAuthenticated && (
                        <Card
                          size="small"
                          style={{
                            marginBottom: 16,
                            background: '#fafff5',
                            border: '1px solid #e8f0e8',
                          }}
                        >
                          <Space direction="vertical" style={{ width: '100%' }}>
                            <Input.TextArea
                              rows={2}
                              placeholder="Ask a question about this product..."
                              value={newQuestion}
                              onChange={(e) => setNewQuestion(e.target.value)}
                            />
                            <Button
                              type="primary"
                              icon={<QuestionCircleOutlined />}
                              loading={submittingQuestion}
                              onClick={handleAskQuestion}
                              disabled={!newQuestion.trim()}
                              style={{ alignSelf: 'flex-end' }}
                            >
                              Submit Question
                            </Button>
                          </Space>
                        </Card>
                      )}
                      {questions.length > 0 ? (
                        <List
                          dataSource={questions}
                          renderItem={(q) => (
                            <List.Item>
                              <List.Item.Meta
                                avatar={
                                  <Avatar
                                    icon={<UserOutlined />}
                                    style={{ backgroundColor: '#52b788' }}
                                  />
                                }
                                title={
                                  <Text strong>
                                    {q.customer?.name ||
                                      q.customer?.full_name ||
                                      q.user?.name ||
                                      q.user?.full_name ||
                                      'Customer'}{' '}
                                    <Text style={{ fontSize: 12, color: '#9ca3af' }}>asked</Text>
                                  </Text>
                                }
                                description={
                                  <div>
                                    <Paragraph style={{ marginBottom: 8 }}>
                                      {q.question || q.comment}
                                    </Paragraph>
                                    {q.answer ? (
                                      <Card
                                        size="small"
                                        style={{
                                          background: '#f0faf0',
                                          border: '1px solid #d1fae5',
                                          marginTop: 8,
                                        }}
                                      >
                                        <Space>
                                          <CheckCircleOutlined style={{ color: '#52b788' }} />
                                          <Text strong style={{ color: '#2d5a3d' }}>
                                            Answer:
                                          </Text>
                                        </Space>
                                        <Paragraph style={{ margin: '4px 0 0', color: '#374151' }}>
                                          {q.answer}
                                        </Paragraph>
                                        {q.answeredBy?.name && (
                                          <Text style={{ fontSize: 11, color: '#9ca3af' }}>
                                            , {q.answeredBy.name}
                                          </Text>
                                        )}
                                      </Card>
                                    ) : (
                                      <Tag
                                        icon={<ClockCircleOutlined />}
                                        color="default"
                                        style={{ fontSize: 12 }}
                                      >
                                        Awaiting provider response
                                      </Tag>
                                    )}
                                  </div>
                                }
                              />
                            </List.Item>
                          )}
                        />
                      ) : (
                        <Empty description="No questions yet. Be the first to ask!" />
                      )}
                    </div>
                  ),
                },
              ]}
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="Provider Info" style={{ marginBottom: 16 }}>
            <Space direction="vertical">
              <Space>
                <UserOutlined />{' '}
                <Text>{product.provider?.name || product.provider_name || 'Current Care'}</Text>
              </Space>
              {product.provider?.phone && (
                <Space>
                  <Text type="secondary">Phone: {product.provider.phone}</Text>
                </Space>
              )}
              {product.provider?.email && (
                <Space>
                  <Text type="secondary">Email: {product.provider.email}</Text>
                </Space>
              )}
            </Space>
          </Card>
          <Card
            title="Service Details"
            extra={product.type === 'service' && <Tag color="blue">Service</Tag>}
          >
            <Space direction="vertical">
              <Text>Category: {product.category}</Text>
              <Text>Type: {product.type || 'Product'}</Text>
              {product.stock > 0 && <Text>Stock: {product.stock}</Text>}
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ProductDetail;
